import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Toaster } from "@/components/ui/sonner";
import PopupCenter from "@/components/PopupCenter";
import ClickToEditText from "@/components/ClickToEditText";
import ChatAlerts from "@/components/chat/ChatAlerts";
import { initSiteText } from "@/lib/siteText";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { auth, api } from "@/lib/api";
import Landing from "@/pages/Landing";
import Editor from "@/pages/Editor";
import Login from "@/pages/Login";
import PaymentReturn from "@/pages/PaymentReturn";
import Pricing from "@/pages/Pricing";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import InstagramTemplateManager from "@/pages/InstagramTemplateManager";
import InstagramUserReview from "@/pages/InstagramUserReview";
import InstagramTeamAccess from "@/pages/InstagramTeamAccess";
import PagesAccess from "@/pages/PagesAccess";
import CategoryBrowse from "@/pages/CategoryBrowse";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import Unsubscribe from "@/pages/Unsubscribe";
import Diagnostics from "@/pages/Diagnostics";
import Portfolio from "@/pages/Portfolio";
import InstagramPostMakerV2 from "@/pages/InstagramPostMakerV2";
import InstagramEntry from "@/pages/InstagramEntry";
import Tools from "@/pages/Tools";
import CvCreator from "@/pages/CvCreator";
import CoverLetter from "@/pages/CoverLetter";
import AnimateImage from "@/pages/AnimateImage";
import AnimateVideo from "@/pages/AnimateVideo";
import DeveloperPortal from "@/pages/DeveloperPortal";
import ApiDocs from "@/pages/ApiDocs";
import WebPage from "@/pages/WebPage";
import HowToUse from "@/pages/HowToUse";
import V5CodeWorkspace from "@/pages/V5CodeWorkspace";
import ManageShell from "@/components/ManageShell";
import UserAnalytics from "@/components/UserAnalytics";
import FontManagement from "@/components/FontManagement";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import YoutubeThumbnail from "@/pages/YoutubeThumbnail";
import MusicStudio from "@/pages/MusicStudio";
import MusicTakedown from "@/pages/MusicTakedown";
import WebPagesDirectory from "@/pages/WebPagesDirectory";
import CricketAdmin from "@/pages/CricketAdmin";
import CricketPublic, { CricketOutput } from "@/pages/CricketPublic";
import Navbar from "@/components/Navbar";
import YtThumbAdmin from "@/components/YtThumbAdmin";
import { Loader2 } from "lucide-react";

function PortfolioRedirect() {
  const [dest, setDest] = useState(undefined);
  useEffect(() => {
    api.portfoliosPublic().then((d) => setDest(d.first ? `/portfolio/${d.first}` : null)).catch(() => setDest(null));
  }, []);
  if (dest === undefined) return <div style={{ minHeight: "100vh", background: "#0A0A0A" }} />;
  if (!dest) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0A0A0A", color: "#fff", fontFamily: "Manrope, sans-serif" }}>No published portfolio yet.</div>;
  return <Navigate to={dest} replace />;
}

function AuthCallback() {
  const done = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.replace("#", "")).get("session_id");
    (async () => {
      try { const u = await auth.session(sid); setUser(u); } catch {}
      window.history.replaceState(null, "", "/");
      navigate("/", { replace: true });
    })();
  }, [navigate, setUser]);
  return <Splash />;
}

const Splash = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="animate-spin text-[#00F0FF]" /></div>
);

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
      <Route path="/payment/success" element={<PaymentReturn mode="success" />} />
      <Route path="/payment/cancel" element={<PaymentReturn mode="cancel" />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/docs" element={<ApiDocs />} />
      <Route path="/web/:slug" element={<WebPage />} />
      <Route path="/pages" element={<WebPagesDirectory />} />
      <Route path="/cricket/admin" element={<Protected><CricketAdmin /></Protected>} />
      <Route path="/cricket/t/:slug" element={<CricketPublic />} />
      <Route path="/cricket/t/:slug/match/:mid" element={<CricketPublic />} />
      <Route path="/cricket/t/:slug/output/:mode" element={<CricketOutput />} />
      <Route path="/how-to-use" element={<HowToUse />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/music-takedown" element={<MusicTakedown />} />
      <Route path="/privacy" element={<Legal />} />
      <Route path="/terms" element={<Legal />} />
      <Route path="/refund" element={<Legal />} />
      <Route path="/diagnostics" element={<Diagnostics />} />
      <Route path="/portfolio" element={<PortfolioRedirect />} />
      <Route path="/portfolio/:slug" element={<Portfolio />} />
      <Route path="/instagram-post-maker" element={<Protected><InstagramEntry /></Protected>} />
      <Route path="/instagram-post-maker-v2" element={<Protected><InstagramPostMakerV2 preview /></Protected>} />
      <Route path="/instagram-v5-workspace" element={<Protected><V5CodeWorkspace /></Protected>} />
      <Route path="/tools" element={<Protected><Tools /></Protected>} />
      <Route path="/tools/instagram-post-maker" element={<Protected><InstagramEntry /></Protected>} />
      <Route path="/tools/cv-creator" element={<Protected><CvCreator /></Protected>} />
      <Route path="/tools/cover-letter" element={<Protected><CoverLetter /></Protected>} />
      <Route path="/tools/animate-image" element={<Protected><AnimateImage /></Protected>} />
      <Route path="/tools/animate-video" element={<Protected><AnimateVideo /></Protected>} />
      <Route path="/tools/youtube-thumbnail" element={<Protected><YoutubeThumbnail /></Protected>} />
      <Route path="/tools/ai-music" element={<Protected><MusicStudio /></Protected>} />
      <Route path="/admin/youtube-thumbnail" element={<Protected><div className="min-h-screen bg-[#0A0A0A] text-white"><Navbar /><div className="mx-auto max-w-[1300px] p-6"><YtThumbAdmin /></div></div></Protected>} />
      <Route path="/developer" element={<Protected><DeveloperPortal /></Protected>} />
      <Route path="/developer/billing" element={<Protected><DeveloperPortal /></Protected>} />
      <Route path="/editor/:id" element={<Protected><Editor /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/instagram-template-manager" element={<Protected><InstagramTemplateManager /></Protected>} />
      <Route path="/instagram-user-review" element={<Protected><InstagramUserReview /></Protected>} />
      <Route path="/instagram-team-access" element={<Protected><InstagramTeamAccess /></Protected>} />
      <Route path="/admin/template-management" element={<Protected><InstagramTemplateManager /></Protected>} />
      <Route path="/admin/template-review" element={<Protected><InstagramUserReview /></Protected>} />
      <Route path="/admin/pages-access" element={<Protected><PagesAccess /></Protected>} />
      <Route path="/admin/user-analytics" element={<Protected><ManageShell slug="user-analytics" title="User Analytics"><UserAnalytics /></ManageShell></Protected>} />
      <Route path="/admin/font-management" element={<Protected><ManageShell slug="font-management" title="Font Management"><FontManagement /></ManageShell></Protected>} />
      <Route path="/templates/:slug" element={<Protected><CategoryBrowse /></Protected>} />
    </Routes>
  );
}

initSiteText();

// Global helpers; hidden on clean broadcast output routes (OBS browser sources).
function SiteOverlays() {
  const { pathname } = useLocation();
  if (/^\/cricket\/t\/[^/]+\/output\//.test(pathname)) return null;
  return (<><PopupCenter /><ClickToEditText /><ChatAlerts /></>);
}

function App() {
  return (
    <div className="App font-ui">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <SiteOverlays />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" theme="dark" richColors />
    </div>
  );
}

export default App;
